// import { ModalConfirmationTextures } from '@components/GenericModals/ModalConfirmationTextures'
import ConfirmationDeleteFrontals from '@components/ModalConfirm/ConfirmationDeleteFrontals'
import ConfirmationDeleteLaterals from '@components/ModalConfirm/ConfirmationDeleteLaterals'
import { useLinkedContext } from '@context/LinkedItemZoneContext'
import useSyncFrontalWithEncimera from '@hooks/useSyncFrontalWithEncimera'
import { getEncimerasFrontales } from '@proxies/getEncimerasFrontales'
import * as Toggle from '@radix-ui/react-toggle'
import { useAppStore } from '@store'
import { LinkedItem, LinkedItemsID, Store } from '@types'
import { API } from '@utils/queryBuilder'
import { useEffect, useRef, useState } from 'react'

export function LinkedButtonsEditor() {
  useSyncFrontalWithEncimera()
  const {
    object,
    modeEditor,
    setModeEditor,
    setAcabado,
    setHighlighted,
    setIsModel,
    setCopiedTexture,
    setIamUp,
    actions: { handleZoneClick },
  } = useLinkedContext()

  const items = useAppStore((s) => s.items)
  const currentObjectID = useAppStore((s) => s.currentObjectID)
  const selected = useAppStore((s) => s.layers[s.currentLayer].selected)
  const drawFrontalsFromSnappedGroups = useAppStore((s) => s.drawFrontalsFromSnappedGroups)
  const deleteFrontals = useAppStore((s) => s.deleteFrontals)

  const deleteLaterals = useAppStore((s) => s.deleteLaterales)
  const drawLateralsForGroups = useAppStore((s) => s.drawLateralesForGroups)
  const id = object?.current?.id
  const getFrontalHeight = useAppStore((s) => s.getFrontalHeight)

  const [frontalNumber, setFrontalNumber] = useState(getFrontalHeight)
  const [frontalsActive, setFrontalsActive] = useState(false)
  const [lateralsActive, setLateralsActive] = useState(false)
  const [showConfirmDeleteLaterals, setShowConfirmDeleteLaterals] = useState(false)
  const [showConfirmDeleteFrontals, setShowConfirmDeleteFrontals] = useState(false)
  const frontalsAutoEnabled = useAppStore((s) => s.frontalsAutoEnabled ?? true)
  const lateralsAutoEnabled = useAppStore((s) => s.lateralsAutoEnabled ?? true)
  const setFrontalsAutoEnabled = useAppStore((s) => s.setFrontalsAutoEnabled)
  const setLateralsAutoEnabled = useAppStore((s) => s.setLateralsAutoEnabled)

  useEffect(() => {
    const hasFrontals = Array.from(items.values()).some((item) => item.catalogID === 'Frontal')
    setFrontalsActive(frontalsAutoEnabled ? hasFrontals : false)
  }, [items, frontalsAutoEnabled])

  useEffect(() => {
    const hasLaterals = Array.from(items.values()).some((item) => {
      const catalogID = item.catalogID?.toString() || ''
      return (
        catalogID.startsWith('LA') ||
        catalogID.startsWith('LB') ||
        catalogID.startsWith('LC') ||
        catalogID.startsWith('CC') ||
        catalogID.startsWith('CB')
      )
    })
    setLateralsActive(lateralsAutoEnabled ? hasLaterals : false)
  }, [items, lateralsAutoEnabled])

  const prevIdRef = useRef(id)
  const prevCurrentObjectIDRef = useRef(currentObjectID)

  const handleFrontalsToggle = async (pressed: boolean) => {
    if (pressed) {
      setFrontalsAutoEnabled?.(true)
      setFrontalsActive(true)
      // Establecer frontalActive ANTES de dibujar para que la herencia automática funcione
      useAppStore.getState().setFrontalActive?.(true)
      await drawFrontalsFromSnappedGroups(frontalNumber, { force: true })

      setTimeout(async () => {
        const currentFrontals = Array.from(useAppStore.getState().items.values()).filter(
          (item) => item.catalogID === 'Frontal',
        )

        // Solo actualizar si no hay frontales (para desactivar si no se pudieron crear)
        if (currentFrontals.length === 0) {
          useAppStore.getState().setFrontalActive?.(false)
        }

        if (currentFrontals.length > 0) {
          try {
            const opcionesFrontales = await getEncimerasFrontales()

            useAppStore.getState().setFrontalesOptions?.(opcionesFrontales)

            const firstFrontal = currentFrontals[0]
            const firstItem = useAppStore.getState().items.get(firstFrontal.id) as
              | LinkedItem
              | undefined
            const frontalTexture = firstItem?.textures?.find(
              (t: any) => t.zoneDisplayName === 'Frontal' || t.modelo === 'Frontal',
            ) as any

            const linkedTextureID = Number(frontalTexture?.linkedTextureID)

            if (!Number.isFinite(linkedTextureID)) {
              console.warn('⚠️ LinkedEditor - No se encontró linkedTextureID para Frontal')
            } else {
              try {
                const info =
                  await API().api.acabadosGetAcabadoZonaModeloAcabadoInfo(linkedTextureID)
                const zonas = (info?.data as any[]) || []

                const allAcabados = zonas?.[0]?.acabados || []

                if (allAcabados.length === 0) {
                  console.warn('⚠️ LinkedEditor - No se encontraron acabados para Frontal')
                } else {
                  const allTextures: any[] = []

                  for (const acabado of allAcabados) {
                    const idAcabadoUnidadModeloAcabado = acabado.idAcabadoUnidadModeloAcabado

                    try {
                      const texResp = await API().api.acabadosGetTexturas(
                        idAcabadoUnidadModeloAcabado,
                      )
                      const textures = (texResp?.data as any[]) || []

                      textures.forEach((tex: any) => {
                        const exists = allTextures.some(
                          (t: any) =>
                            t?.nombre === tex?.nombre || t?.descripcion === tex?.descripcion,
                        )
                        if (!exists) {
                          allTextures.push(tex)
                        }
                      })
                    } catch (error) {
                      console.error(`❌ Error cargando texturas para ${acabado.acabado}:`, error)
                    }
                  }

                  useAppStore.getState().setFrontalesTexturesOptions?.(allTextures)

                  const allItems = useAppStore.getState().items
                  const encimera = Array.from(allItems.values()).find(
                    (it: any) =>
                      it?.type === 'linkedSon' &&
                      (it?.typeOfSon === 'Encimera' || it?.description === 'Encimera'),
                  ) as any

                  const encimeraTex = encimera?.textures?.find(
                    (t: any) => t.zoneDisplayName === 'Encimera',
                  ) as any
                  const encimeraTextureName: string | undefined = encimeraTex?.name?.trim()

                  if (encimeraTextureName) {
                    const mapping = useAppStore.getState().frontalesOptions as
                      | Record<string, string>
                      | undefined
                    const frontalCode = mapping ? mapping[encimeraTextureName] : undefined

                    if (frontalCode) {
                      const finalTextures =
                        (useAppStore.getState().frontalesTexturesOptions as any[]) || []

                      const targetTex = finalTextures.find(
                        (t: any) =>
                          t?.nombre?.trim() === frontalCode?.trim() ||
                          t?.descripcion?.trim() === frontalCode?.trim(),
                      )

                      if (targetTex) {
                        useAppStore.setState((store) => {
                          const frontalEntries = Array.from(store.items.entries()).filter(
                            ([, v]: [string, any]) =>
                              v?.type === 'linkedItem' && v?.catalogID === 'Frontal',
                          ) as [string, any][]

                          frontalEntries.forEach(([fid, fitem]) => {
                            const texIdx = fitem?.textures?.findIndex(
                              (tx: any) =>
                                tx.zoneDisplayName === 'Frontal' || tx.modelo === 'Frontal',
                            )
                            if (texIdx != null && texIdx >= 0) {
                              const prev = fitem.textures[texIdx]
                              const mini =
                                targetTex.urlMiniatura || targetTex.miniatura || prev?.img
                              fitem.textures[texIdx] = {
                                ...prev,
                                name: targetTex.nombre ?? targetTex.descripcion ?? prev?.name,
                                url: targetTex.url ?? prev?.url,
                                description: targetTex.descripcion ?? prev?.description,
                                acabado: prev?.acabado ?? 'Frontal',
                                img: mini,
                                id: targetTex.identificadorTextura ?? prev?.id,
                                searchID: targetTex.idTexturas ?? prev?.searchID,
                              }
                              // actualizar imagen del item para vistas/listas
                              if (mini) {
                                fitem.image = mini
                              }
                              // sincronizar zones.material de la zona Frontal
                              const zoneName = prev?.zoneName || fitem.textures[texIdx]?.zoneName
                              if (zoneName && fitem.zones && fitem.zones[zoneName]) {
                                const matPrev = fitem.zones[zoneName].material || {}
                                fitem.zones[zoneName].material = {
                                  ...matPrev,
                                  name: targetTex.nombre ?? targetTex.descripcion ?? matPrev?.name,
                                  description: targetTex.descripcion ?? matPrev?.description,
                                  url: targetTex.url ?? matPrev?.url,
                                  img: mini ?? matPrev?.img,
                                  acabado: matPrev?.acabado ?? 'Frontal',
                                  groupID: targetTex.idGruposDeTexturas ?? matPrev?.groupID,
                                  id: targetTex.identificadorTextura ?? matPrev?.id,
                                  searchID: targetTex.idTexturas ?? matPrev?.searchID,
                                }
                              }
                            }
                          })
                        })

                        try {
                          useAppStore.getState().setFrontalesTexturesOptions?.(finalTextures)

                          const frontalSelected = useAppStore
                            .getState()
                            .items.get(useAppStore.getState().currentObjectID as any) as any
                          if (frontalSelected && frontalSelected.catalogID === 'Frontal') {
                            const appliedTex = frontalSelected.textures?.find(
                              (tx: any) =>
                                tx.zoneDisplayName === 'Frontal' || tx.modelo === 'Frontal',
                            )
                            if (appliedTex) {
                              const newHighlighted = handleConcatStrings(
                                frontalSelected.id,
                                appliedTex.zoneName,
                              )
                              setHighlighted(newHighlighted)
                              await handleZoneClick(appliedTex)
                            }
                          }
                        } catch (e) {
                          console.warn(
                            '⚠️ LinkedEditor - No se pudo sincronizar rightInfo/selección:',
                            e,
                          )
                        }
                      } else {
                        console.warn(
                          '⚠️ LinkedEditor - No se encontró textura en frontalesTexturesOptions para:',
                          frontalCode,
                        )
                      }
                    } else {
                      console.warn(
                        '⚠️ LinkedEditor - No mapping en frontalesOptions para encimera:',
                        encimeraTextureName,
                      )
                    }
                  } else {
                    console.warn(
                      '⚠️ LinkedEditor - No se pudo determinar la textura de Encimera seleccionada',
                    )
                  }
                }
              } catch (e) {
                console.error('❌ LinkedEditor - Error obteniendo rightInfo de Frontal:', e)
              }
            }
          } catch (error) {
            console.error('❌ LinkedEditor - Error obteniendo opciones de frontales:', error)
          }
        } else {
          console.log('LinkedEditor - No hay frontales, no se hace llamada API')
        }
      }, 1000)
    } else {
      setShowConfirmDeleteFrontals(true)
    }
  }

  const handleConfirmDeleteFrontals = () => {
    // Ahora sí desactivar y eliminar
    setFrontalsAutoEnabled?.(false)
    setFrontalsActive(false)
    deleteFrontals()
    useAppStore.getState().setFrontalActive?.(false)
    setShowConfirmDeleteFrontals(false)
  }

  const handleCancelDeleteFrontals = () => {
    setFrontalsAutoEnabled?.(false)
    setFrontalsActive(false)
    useAppStore.getState().setFrontalActive?.(false)
    setShowConfirmDeleteFrontals(false)
    // El toggle volverá automáticamente a su estado anterior porque frontalsActive no cambió
  }

  const handleLateralsToggle = async (pressed: boolean) => {
    if (pressed) {
      setLateralsAutoEnabled?.(true)
      setLateralsActive(true)
      const drawLaterales = useAppStore.getState().drawLateralesForGroups
      if (drawLaterales) {
        await drawLaterales({ force: true })
      } else {
        console.error('❌ drawLateralesForGroups no está disponible en el store')
      }
    } else {
      // Mostrar modal de confirmación antes de desactivar
      setShowConfirmDeleteLaterals(true)
    }
  }

  const handleConfirmDeleteLaterals = () => {
    // Ahora sí desactivar y eliminar
    setLateralsAutoEnabled?.(false)
    setLateralsActive(false)
    deleteLaterals()
    setShowConfirmDeleteLaterals(false)
  }

  const handleCancelDeleteLaterals = () => {
    setLateralsAutoEnabled?.(false)
    setLateralsActive(false)
    setShowConfirmDeleteLaterals(false)
    // El toggle volverá automáticamente a su estado anterior porque lateralsActive no cambió
  }

  const handleFrontalNumberChange = async (newValue: number) => {
    setFrontalNumber(newValue)

    if (frontalsActive) {
      setFrontalsActive(false)
    }
  }

  const t = (str: string) => (str == null ? '' : String(str))

  const armazonBlancoValue =
    id === currentObjectID && localArmazonBlanco !== null
      ? localArmazonBlanco
      : currentItemFromStore !== undefined
        ? currentItemFromStore
        : object.current && object.current.type === 'linkedItem'
          ? getActualArmazonBlanco(object.current as LinkedItem)
          : false
  // Usar armazonBlancoValue o false por defecto
  const armazonBlanco = armazonBlancoValue ?? false
  async function ensureGLBLoaded(
    catalogID: string,
    referenciaBlender: string,
    newUnidadData: FEUnidadDTO,
  ) {
    const sceneGLB = useAppStore.getState().sceneGLB
    if (!sceneGLB.has(catalogID)) {
      const loader = await import('@utils/sceneObjects/setGLTFLoader').then((mod) =>
        mod.setGLTFLoader(),
      )
      const glb = await loader.loadAsync(referenciaBlender)
      useAppStore.setState((store) => {
        store.sceneGLB.set(catalogID, {
          glb,
          backGLBInfo: newUnidadData,
        })
      })
    }
  }

  async function changeGolaForItem(itemId: string, shouldUpdateUI = false) {
    const itemBefore = useAppStore.getState().items.get(itemId as LinkedItemsID)

    if (!itemBefore || itemBefore.type !== 'linkedItem') {
      return
    }
    const linkedItemBefore = itemBefore as LinkedItem

    // Elegir id de salida según sufijo actual (LA/LI/Ru/R2) si está en Gola OJO
    const catalog = String(linkedItemBefore.catalogID || '')
    const isGolaNow = catalog.includes(' GO')
    let idCambioToUse: number | string | null = null

    // Los patrones buscan espacios antes de LA/LI/R2/Ru seguidos de GO
    // Ejemplos: "PE KGBP801202 R2 GO" o "KGBP801202 R2 GO"
    if (isGolaNow) {
      if (/\sLA\sGO/i.test(catalog) && (linkedItemBefore as any).idCambioLara) {
        idCambioToUse = (linkedItemBefore as any).idCambioLara
      } else if (/\sLI\sGO/i.test(catalog) && (linkedItemBefore as any).idCambioLineal) {
        idCambioToUse = (linkedItemBefore as any).idCambioLineal
      } else if (/\sR2\sGO/i.test(catalog) && (linkedItemBefore as any).idCambioRutik2) {
        idCambioToUse = (linkedItemBefore as any).idCambioRutik2
      } else if (/\sRu\sGO/i.test(catalog) && (linkedItemBefore as any).idCambioRutik) {
        idCambioToUse = (linkedItemBefore as any).idCambioRutik
      }
    }

    if (!idCambioToUse) {
      const isPetroleo = catalog.startsWith('PE ')
      const currentArmazonBlanco = isPetroleo ? false : (linkedItemBefore.ArmazonBlanco ?? true) // Por defecto blanco si no está definido

      // idCambioGolaBlanco e idCambioGolaPetroleo funcionan en ambas direcciones (Gola ↔ No Gola)
      // Solo dependen del tipo de armazón, NO de si estás en Gola o No Gola
      // idCambioArmazon es SOLO para cambiar el armazón (blanco ↔ petróleo), NO para cambiar Gola
      if (currentArmazonBlanco) {
        // Armazón es blanco → usar idCambioGolaBlanco (funciona Gola ↔ No Gola)
        if (linkedItemBefore.idcambiogolablanco) {
          idCambioToUse = linkedItemBefore.idcambiogolablanco
        } else {
          console.error('No se encontró idcambiogolablanco para cambiar Gola')
          return
        }
      } else {
        // Armazón es petróleo → usar idCambioGolaPetroleo (funciona Gola ↔ No Gola)
        if (linkedItemBefore.idcambiogolapetroleo) {
          idCambioToUse = linkedItemBefore.idcambiogolapetroleo
        } else {
          console.error('No se encontró idcambiogolapetroleo para cambiar Gola')
          return
        }
      }
    }

    try {
      const response = await API().api.unidadesGetUnidadGet(
        idCambioToUse as number,
        String(idCambioToUse),
        {
          headers: {
            'Accept-Language': 'ES',
          },
        },
      )

      if (!response || !response.data) {
        console.error('No se pudo obtener la unidad de cambio gola')
        return
      }

      const newUnidadData: FEUnidadDTO = response.data

      await ensureGLBLoaded(
        String(newUnidadData.codigo || ''),
        String(newUnidadData.referenciaBlender || ''),
        newUnidadData,
      )

      await updateLinkedItemGola(itemId as LinkedItemsID, newUnidadData)

      // Solo actualizar el UI si shouldUpdateUI es true (solo para el item seleccionado)
      if (shouldUpdateUI) {
        await new Promise((resolve) => setTimeout(resolve, 100))

        const updatedItem = useAppStore.getState().items.get(itemId as LinkedItemsID)
        if (updatedItem && updatedItem.type === 'linkedItem' && updatedItem.textures) {
          // Buscar la textura de Puerta para hacer click automático
          const doorTexture = updatedItem.textures.find((t: any) => t.zoneDisplayName === 'Puerta')

          if (doorTexture && doorTexture.acabado) {
            setAcabado(doorTexture.acabado)

            // PRIMERO actualiza highlighted para que la zona se marque visualmente
            const newHighlighted = handleConcatStrings(itemId, doorTexture.zoneName)
            setHighlighted(newHighlighted)
            setCopiedTexture(null)
            setIsModel(false)
            setIamUp(false)

            await handleZoneClick(doorTexture)
          } else if (updatedItem.textures.length > 0) {
            // Si no hay Puerta, usar la primera textura disponible
            const firstTexture = updatedItem.textures[0]
            console.log('firstTexture', firstTexture)

            const newHighlighted = handleConcatStrings(itemId, firstTexture.zoneName)
            setHighlighted(newHighlighted)
            setCopiedTexture(null)
            setIsModel(false)
            setIamUp(false)

            await handleZoneClick(firstTexture)
          } else {
            console.warn('⚠️ No door texture found or no acabado:', updatedItem.textures)
          }
        }
      }
    } catch (error) {
      console.error('Error al cambiar gola para item', itemId, ':', error)
    }
  }

  async function handleChangeGola() {
    const currentItem = useAppStore.getState().items.get(id as LinkedItemsID)

    if (!currentItem || currentItem.type !== 'linkedItem') {
      return
    }

    const linkedItem = currentItem as LinkedItem

    const isCurrentlyGola = isGolaUnit(
      (linkedItem.catalogID as string) || '',
      linkedItem.referencia || '',
    )

    if (isCurrentlyGola) {
      // Si está en Gola, necesita idcambioarmazon para cambiar a No Gola
      if (!linkedItem.idcambioarmazon) {
        console.error('No se puede cambiar de Gola a No Gola: falta idcambioarmazon')
        return
      }
    } else {
      const isPetroleo = String(linkedItem.catalogID || '').startsWith('PE ')
      if (!linkedItem.idcambiogolablanco && (!isPetroleo || !linkedItem.idcambiogolapetroleo)) {
        console.error('No se puede cambiar a Gola: falta idcambiogolablanco o idcambiogolapetroleo')
        return
      }
    }

    const newGolaActive = !isCurrentlyGola
    // Guardar el modeEditor actual y el id seleccionado para restaurarlos después
    const savedModeEditor = modeEditor
    const selectedItemId = id as string

    setIsGolaChanging(true)
    setGolaActive(newGolaActive)

    setTimeout(async () => {
      try {
        if (
          modeEditor === 'All' ||
          modeEditor === 'Selected' ||
          modeEditor === 'MBJ' ||
          modeEditor === 'MAL' ||
          modeEditor === 'COL'
        ) {
          let itemsToChange: string[] = []

          if (modeEditor === 'All') {
            itemsToChange = Array.from(items.values())
              .filter((item): item is LinkedItem => {
                if (item.type !== 'linkedItem') return false
                const linkedItem = item as LinkedItem
                if (!linkedItem.idcambiogolablanco) return false

                const itemIsGola = isGolaUnit(
                  (linkedItem.catalogID as string) || '',
                  linkedItem.referencia || '',
                )

                // Solo incluir si el estado actual es diferente al deseado
                // Si queremos cambiar a gola (newGolaActive = true), solo cambiar las que NO son gola
                // Si queremos cambiar a no gola (newGolaActive = false), solo cambiar las que SÍ son gola
                return itemIsGola !== newGolaActive
              })
              .map((item) => item.id)
          } else if (modeEditor === 'Selected') {
            itemsToChange = Array.from(selected as Set<LinkedItemsID>).filter((selectedId) => {
              const selectedItem = items.get(selectedId)
              if (!selectedItem || selectedItem.type !== 'linkedItem') return false
              const linkedItem = selectedItem as LinkedItem
              if (!linkedItem.idcambiogolablanco) return false

              const itemIsGola = isGolaUnit(
                (linkedItem.catalogID as string) || '',
                linkedItem.referencia || '',
              )

              return itemIsGola !== newGolaActive
            }) as string[]
          } else if (modeEditor === 'MBJ') {
            itemsToChange = Array.from(items.values())
              .filter((item): item is LinkedItem => {
                if (item.type !== 'linkedItem') return false
                const linkedItem = item as LinkedItem
                if (linkedItem.tipoNormalizado !== 'MBJ' || !linkedItem.idcambiogolablanco)
                  return false

                const itemIsGola = isGolaUnit(
                  (linkedItem.catalogID as string) || '',
                  linkedItem.referencia || '',
                )

                return itemIsGola !== newGolaActive
              })
              .map((item) => item.id)
          } else if (modeEditor === 'MAL') {
            itemsToChange = Array.from(items.values())
              .filter((item): item is LinkedItem => {
                if (item.type !== 'linkedItem') return false
                const linkedItem = item as LinkedItem
                if (linkedItem.tipoNormalizado !== 'MAL' || !linkedItem.idcambiogolablanco)
                  return false

                const itemIsGola = isGolaUnit(
                  (linkedItem.catalogID as string) || '',
                  linkedItem.referencia || '',
                )

                return itemIsGola !== newGolaActive
              })
              .map((item) => item.id)
          } else if (modeEditor === 'COL') {
            itemsToChange = Array.from(items.values())
              .filter((item): item is LinkedItem => {
                if (item.type !== 'linkedItem') return false
                const linkedItem = item as LinkedItem
                if (linkedItem.tipoNormalizado !== 'COL' || !linkedItem.idcambiogolablanco)
                  return false

                const itemIsGola = isGolaUnit(
                  (linkedItem.catalogID as string) || '',
                  linkedItem.referencia || '',
                )

                return itemIsGola !== newGolaActive
              })
              .map((item) => item.id)
          }

          // Cambiar solo los items que necesitan cambiar, SIN actualizar el UI (shouldUpdateUI = false para todos)
          await Promise.all(itemsToChange.map((itemId) => changeGolaForItem(itemId, false)))

          // Restaurar el modeEditor después de todos los cambios
          if (setModeEditor && savedModeEditor) {
            setModeEditor(savedModeEditor)
          }

          // Después de todos los cambios, actualizar el UI solo para el item seleccionado
          // con la textura de Puerta
          // Esperar un poco más para asegurar que todos los cambios se hayan propagado
          await new Promise((resolve) => setTimeout(resolve, 500))

          const updatedItem = useAppStore.getState().items.get(selectedItemId as LinkedItemsID)
          if (updatedItem && updatedItem.type === 'linkedItem' && updatedItem.textures) {
            const doorTexture = updatedItem.textures.find(
              (t: any) => t.zoneDisplayName === 'Puerta',
            )

            if (doorTexture && doorTexture.acabado) {
              // Replica exactamente lo que hace handlePropertyClick en LinkedObjectZonesEditor:
              const newHighlighted = handleConcatStrings(selectedItemId, doorTexture.zoneName)
              setHighlighted(newHighlighted)
              setCopiedTexture(null)
              setIsModel(false)
              setIamUp(false)
              // Esperar múltiples frames para que el estado se propague completamente
              await new Promise((resolve) => requestAnimationFrame(resolve))
              await new Promise((resolve) => requestAnimationFrame(resolve))

              // handleZoneClick es asíncrono y actualiza rightInfo a través de handleGroupClick
              await handleZoneClick(doorTexture)

              //    - El estado se propague completamente (rightInfo, selectedTexture)
              //    - El DOM se renderice con la clase .elElegido
              //    - useScrollOnLoad pueda encontrar el elemento y hacer scroll
              await new Promise((resolve) => setTimeout(resolve, 500))
            } else if (updatedItem.textures.length > 0) {
              const firstTexture = updatedItem.textures[0]
              // Replicar exactamente lo que hace handlePropertyClick en LinkedObjectZonesEditor:
              const newHighlighted = handleConcatStrings(selectedItemId, firstTexture.zoneName)
              setHighlighted(newHighlighted)
              setCopiedTexture(null)
              setIsModel(false)
              setIamUp(false)
              // Esperar múltiples frames para que el estado se propague completamente
              await new Promise((resolve) => requestAnimationFrame(resolve))
              await new Promise((resolve) => requestAnimationFrame(resolve))
              await handleZoneClick(firstTexture)
              // Esperar más tiempo para que el DOM se actualice con la clase .elElegido
              await new Promise((resolve) => setTimeout(resolve, 500))
            }
          }

          const finalUpdatedItem = useAppStore.getState().items.get(id as LinkedItemsID)
          if (finalUpdatedItem && finalUpdatedItem.type === 'linkedItem') {
            const linkedItem = finalUpdatedItem as LinkedItem
            const isGola = isGolaUnit(
              (linkedItem.catalogID as string) || '',
              linkedItem.referencia || '',
            )
            setGolaActive(isGola)
          }
          // Permitir que el useEffect vuelva a funcionar después de un pequeño delay
          // para asegurar que todos los cambios se hayan propagado
          setTimeout(() => {
            setIsGolaChanging(false)
          }, 500)
          return
        }

        if (modeEditor === 'Single') {
          await changeGolaForItem(id as string, true)

          const updatedItem = useAppStore.getState().items.get(id as LinkedItemsID)
          if (updatedItem && updatedItem.type === 'linkedItem') {
            const linkedItem = updatedItem as LinkedItem
            const isGola = isGolaUnit(
              (linkedItem.catalogID as string) || '',
              linkedItem.referencia || '',
            )
            setGolaActive(isGola)
          }

          setTimeout(() => {
            setIsGolaChanging(false)
          }, 500)
        }
      } catch (error) {
        // En caso de error, revertir el estado y permitir que el useEffect vuelva a funcionar
        console.error('Error en handleChangeGola:', error)
        // Restaurar el modeEditor en caso de error
        if (setModeEditor && savedModeEditor) {
          setModeEditor(savedModeEditor)
        }
        const currentItemAfterError = useAppStore.getState().items.get(id as LinkedItemsID)
        if (currentItemAfterError && currentItemAfterError.type === 'linkedItem') {
          const linkedItemAfterError = currentItemAfterError as LinkedItem
          const isGolaAfterError = isGolaUnit(
            (linkedItemAfterError.catalogID as string) || '',
            linkedItemAfterError.referencia || '',
          )
          setGolaActive(isGolaAfterError)
        }
        setIsGolaChanging(false)
      }
    }, 0)
  }

  async function handleChangeArmazon() {
    const currentItem = useAppStore.getState().items.get(id as LinkedItemsID)

    if (!currentItem || currentItem.type !== 'linkedItem') {
      return
    }

    // const linkedItem = currentItem as LinkedItem

    if (!currentItem) {
      return
    }

    // Usar el valor real del store, no solo el catalogID
    // El catalogID puede indicar petróleo inicialmente, pero ArmazonBlanco es el estado real
    const currentArmazonBlanco =
      localArmazonBlanco ??
      (currentItem && currentItem.type === 'linkedItem'
        ? (currentItem as LinkedItem).ArmazonBlanco
        : false)

    const newArmazonBlanco = !currentArmazonBlanco

    setLocalArmazonBlanco(newArmazonBlanco)

    setTimeout(async () => {
      async function fetchUnidadByIdCambioArmazon(idCambioArmazon: string | number) {
        const resp = await API().api.unidadesGetUnidadGet(
          idCambioArmazon as any,
          String(idCambioArmazon),
          { headers: { 'Accept-Language': 'ES' } },
        )
        return (resp?.data as FEUnidadDTO) || undefined
      }
      async function changeArmazonForItem(itemId: string, targetArmazonBlanco?: boolean) {
        const itemBefore = useAppStore.getState().items.get(itemId as LinkedItemsID)

        if (!itemBefore || itemBefore.type !== 'linkedItem') {
          return
        }
        const linkedItemBefore = itemBefore as LinkedItem

        const currentArmazonBlanco = getActualArmazonBlanco(linkedItemBefore)

        if (targetArmazonBlanco !== undefined) {
          if (currentArmazonBlanco === targetArmazonBlanco) {
            return
          }
        }

        try {
          const idCambioArmazon =
            (linkedItemBefore as any).idcambioarmazon ||
            (linkedItemBefore as any).idCambioArmazon ||
            (linkedItemBefore as any).unidad?.idCambioArmazon ||
            (linkedItemBefore as any).FEUnidadDTO?.idCambioArmazon

          if (!idCambioArmazon) {
            return
          }

          const newUnidadData: FEUnidadDTO | undefined =
            await fetchUnidadByIdCambioArmazon(idCambioArmazon)

          if (!newUnidadData) {
            return
          }

          // Usar versión optimizada que NO carga el GLB, solo actualiza texturas de Armazon
          const updateLinkedItemArmazonOptimized =
            useAppStore.getState().updateLinkedItemArmazonOptimized
          await updateLinkedItemArmazonOptimized(itemId as LinkedItemsID, newUnidadData)
        } catch (error) {
          console.error(
            '🔴 [CHANGE ARMAZON] Error al cambiar armazón para item',
            itemId,
            ':',
            error,
          )
        }
      }

      const isLinkedItemVitrina = (candidate: any): candidate is LinkedItem => {
        if (!candidate || candidate.type !== 'linkedItem') return false

        const puertaTexture = candidate.textures?.find((t: any) => t.zoneDisplayName === 'Puerta')
        const modelo = puertaTexture?.modelo?.toLowerCase() ?? ''
        const catalogID = String(candidate.catalogID ?? '').toUpperCase()

        return (
          modelo.includes('vitrina') ||
          catalogID.startsWith('KABV') ||
          catalogID.startsWith('KAPV') ||
          catalogID.startsWith('PE KABV') ||
          catalogID.startsWith('PE KAPV')
        )
      }

      const currentLinkedItem =
        currentItem && currentItem.type === 'linkedItem' ? (currentItem as LinkedItem) : null
      const currentIsVitrina = currentLinkedItem ? isLinkedItemVitrina(currentLinkedItem) : false

      if (
        modeEditor === 'All' ||
        modeEditor === 'Selected' ||
        modeEditor === 'MBJ' ||
        modeEditor === 'MAL' ||
        modeEditor === 'COL'
      ) {
        const selectedItemArmazonBlanco =
          currentItem && currentItem.type === 'linkedItem'
            ? getActualArmazonBlanco(currentItem as LinkedItem)
            : undefined
        const targetArmazonBlanco =
          selectedItemArmazonBlanco !== undefined ? !selectedItemArmazonBlanco : true
        let itemsToChange: string[] = []
        if (modeEditor === 'All' || (modeEditor === 'Selected' && currentIsVitrina)) {
          itemsToChange = Array.from(items.values())
            .filter((item): item is LinkedItem => {
              if (item.type !== 'linkedItem') return false
              const linkedItem = item as LinkedItem

              // Verificar el estado actual del armazón de esta unidad usando la función helper
              // que prioriza el catalogID sobre ArmazonBlanco para evitar inconsistencias
              const itemArmazonBlanco = getActualArmazonBlanco(linkedItem)

              // Solo incluir si el estado actual es diferente al deseado
              // Si queremos cambiar a blanco (targetArmazonBlanco = true), solo cambiar las que NO son blanco
              // Si queremos cambiar a petróleo (targetArmazonBlanco = false), solo cambiar las que SÍ son blanco
              return itemArmazonBlanco !== targetArmazonBlanco
            })
            .map((item) => item.id)
        } else if (modeEditor === 'Selected') {
          itemsToChange = Array.from(selected as Set<LinkedItemsID>).filter((selectedId) => {
            const selectedItem = items.get(selectedId)
            if (!selectedItem || selectedItem.type !== 'linkedItem') return false
            const linkedItem = selectedItem as LinkedItem

            const itemArmazonBlanco = getActualArmazonBlanco(linkedItem)

            return itemArmazonBlanco !== targetArmazonBlanco
          }) as string[]
        } else if (modeEditor === 'MBJ') {
          itemsToChange = Array.from(items.values())
            .filter((item): item is LinkedItem => {
              if (item.type !== 'linkedItem') return false
              const linkedItem = item as LinkedItem
              if (linkedItem.tipoNormalizado !== 'MBJ') return false

              const itemArmazonBlanco = getActualArmazonBlanco(linkedItem)

              // Solo incluir si el estado actual es diferente al deseado
              return itemArmazonBlanco !== targetArmazonBlanco
            })
            .map((item) => item.id)
        } else if (modeEditor === 'MAL') {
          itemsToChange = Array.from(items.values())
            .filter((item): item is LinkedItem => {
              if (item.type !== 'linkedItem') return false
              const linkedItem = item as LinkedItem
              if (linkedItem.tipoNormalizado !== 'MAL') return false

              const itemArmazonBlanco = getActualArmazonBlanco(linkedItem)

              return itemArmazonBlanco !== targetArmazonBlanco
            })
            .map((item) => item.id)
        } else if (modeEditor === 'COL') {
          itemsToChange = Array.from(items.values())
            .filter((item): item is LinkedItem => {
              if (item.type !== 'linkedItem') return false
              const linkedItem = item as LinkedItem
              if (linkedItem.tipoNormalizado !== 'COL') return false

              // Verificar el estado actual del armazón de esta unidad usando la función helper
              const itemArmazonBlanco = getActualArmazonBlanco(linkedItem)

              return itemArmazonBlanco !== targetArmazonBlanco
            })
            .map((item) => item.id)
        }

        let firstToChange: LinkedItem | null = null
        for (const itemId of itemsToChange) {
          const item = useAppStore.getState().items.get(itemId as LinkedItemsID)
          if (
            item &&
            item.type === 'linkedItem' &&
            (item as LinkedItem).ArmazonBlanco !== targetArmazonBlanco
          ) {
            firstToChange = item as LinkedItem
            break
          }
        }
        if (firstToChange) {
          const idCambioArmazon =
            (firstToChange as any).idcambioarmazon ||
            (firstToChange as any).idCambioArmazon ||
            (firstToChange as any).unidad?.idCambioArmazon ||
            (firstToChange as any).FEUnidadDTO?.idCambioArmazon

          if (idCambioArmazon) {
            const data = await fetchUnidadByIdCambioArmazon(idCambioArmazon)
            if (data?.codigo && data?.referenciaBlender) {
              await ensureGLBLoaded(data.codigo, data.referenciaBlender, data)
            }
          }
        }

        // Usar Promise.allSettled para asegurar que todos los items se procesen,
        // incluso si algunos fallan
        const results = await Promise.allSettled(
          itemsToChange.map((itemId) => changeArmazonForItem(itemId, targetArmazonBlanco)),
        )

        // Log errores si los hay, pero continua con el proceso
        if (results && Array.isArray(results)) {
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              console.error(
                `🔴 [HANDLE CHANGE ARMAZON] Error al cambiar armazón para item ${itemsToChange[index]}:`,
                result.reason,
              )
            }
          })
        }
        return
      }
      if (modeEditor === 'Single') {
        await changeArmazonForItem(id as string)
      }
    }, 0)
  }

  return (
    <div className='mb-4 ml-1 mt-auto flex content-end '>
      {/* <div className='w-[288px] rounded-lg bg-C/Color10 p-2 shadow-md'> */}
      {/* <button
          className='w-[278px] cursor-pointer rounded-md border border-solid border-C/Color7 bg-C/Color3 py-0.5 text-[10px] text-C/Color1'
          onClick={() =>
            openModalWithContent(
              `${'¿Quieres copiar el acabado'} ${t(selectedTexture?.id)} ${'de la zona'} ${t(
                selectedTexture?.zoneDisplayName,
              )} ${'al resto de las zonas de la unidad'} ${objectName}?`,
              handleConfirmTextures,
            )
          }
        >
          {'Copiar acabados a todas las zonas'}
        </button> */}
      {/* COMENTADO PROVISIONAL */}
      {/* <button
          className='w-[278px] cursor-pointer rounded-md border border-solid border-C/Color7 bg-C/Color3 py-0.5 text-[10px] text-C/Color1'
          onClick={() =>
            openModalWithContent(
              `${'Quiere copiar el acabado seleccionado de la unidad'} ${objectName}, ${'en todas las zonas con el mismo nombre de todas las unidades del diseño?'}`,
              handleApplyToAllItems,
            )
          }
        >
          {'Copiar acabados a todas las piezas del proyecto'}
        </button> */}
      {/* </div> */}

      <div className='-mb-1.5  ml-3'>
        <div className='ml-14 text-base font-bold text-C/Color5'>{'Color Módulo'} </div>
        <div className='-mb-[8px] ml-4 mt-1.5 flex flex-row items-center justify-between pb-2 text-base font-bold text-C/Color3'>
          <div className='absolute bottom-1.5 right-[47rem] flex items-center gap-3'>
            <span className='-mr-[183px] -mt-[60px] text-base font-bold text-C/Color5'>
              {'Laterales'}
            </span>
            <div className=' flex items-center gap-3'>
              <span>{'Desactivado'}</span>
              <Toggle.Root
                pressed={lateralsActive}
                onPressedChange={(pressed) => {
                  handleLateralsToggle(pressed)
                }}
                className={`h-6 w-12 rounded-full border-2 border-C/Color3 transition-colors duration-200
                    ${lateralsActive ? 'bg-C/Color3' : 'bg-C/Color10'}`}
                aria-label='Toggle Laterals'
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200
                      ${lateralsActive ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </Toggle.Root>
              <span>{'Activado'}</span>
            </div>
          </div>

          

          <div className='absolute bottom-1.5 right-56 mr-60 flex flex-col items-center gap-1'>
            <div className='flex items-center gap-3'>
              <span className='text-base font-bold text-C/Color5'>{'Altura frontales'}</span>
              <input
                type='number'
                value={frontalNumber}
                onChange={(e) => handleFrontalNumberChange(Number(e.target.value))}
                className='w-16 rounded-md border border-solid border-C/Color7 bg-C/Color10 px-2 text-right text-[12px] text-C/Color5 focus:border-C/Color3 focus:outline-none'
                min={10}
              />
            </div>

            <div className='flex items-center gap-3'>
              <span>{'Desactivado'}</span>
              <Toggle.Root
                pressed={frontalsActive}
                onPressedChange={(pressed) => {
                  handleFrontalsToggle(pressed)
                }}
                className={`h-6 w-12 rounded-full border-2 border-C/Color3 transition-colors duration-200
          ${frontalsActive ? 'bg-C/Color3' : 'bg-C/Color10'}`}
                aria-label='Toggle Frontals'
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200
            ${frontalsActive ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </Toggle.Root>
              <span>{'Activado'} </span>
            </div>
          </div>
        </div>

        {/* {isModalOpen && (
          <ModalConfirmationTextures
            text={modalContent.text}
            onConfirm={modalContent.onConfirm}
            closeModal={() => setIsModalOpen(false)}
          />
        )} */}
        {showConfirmDeleteLaterals && (
          <ConfirmationDeleteLaterals
            isOpen={showConfirmDeleteLaterals}
            onClose={handleCancelDeleteLaterals}
            onConfirm={handleConfirmDeleteLaterals}
          />
        )}
        {showConfirmDeleteFrontals && (
          <ConfirmationDeleteFrontals
            isOpen={showConfirmDeleteFrontals}
            onClose={handleCancelDeleteFrontals}
            onConfirm={handleConfirmDeleteFrontals}
          />
        )}
      </div>
    </div>
  )
}
